import React, { useState } from 'react';
import { Modal, Typography, Button, Tabs } from '@crisellevaldez/speculo-ui';
import { Organization } from '../../../types/organization.types';
import { Search, Link2, Globe, Bot, GraduationCap } from 'lucide-react';
import { WebsiteBrandingSection } from './onboardingData/WebsiteBrandingSection';
import { AIAssistantSection } from './onboardingData/AIAssistantSection';
import { IDXMLSSection } from './onboardingData/IDXMLSSection';
import { CRMSection } from './onboardingData/CRMSection';
import { CrashCourseSection } from './onboardingData/CrashCourseSection';

interface OnboardingDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: Organization;
  onRefresh?: () => void;
}

type TabValue = 'website' | 'ai' | 'idx' | 'crm' | 'crashCourse';

const tabs = [
  { value: 'website', label: 'Website', icon: <Globe className="h-4 w-4" /> },
  { value: 'ai', label: 'AI Assistant', icon: <Bot className="h-4 w-4" /> },
  { value: 'idx', label: 'IDX/MLS', icon: <Search className="h-4 w-4" /> },
  { value: 'crm', label: 'CRM', icon: <Link2 className="h-4 w-4" /> },
  { value: 'crashCourse', label: 'Strategy Call', icon: <GraduationCap className="h-4 w-4" /> },
];

export const OnboardingDataModal: React.FC<OnboardingDataModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  onRefresh,
}) => {
  const [selectedTab, setSelectedTab] = useState<TabValue>('website');

  if (!selectedOrg) return null;

  const onboardingSetup = selectedOrg.onboardingSetup || {};
  const onboardingData = (selectedOrg as any).onboardingData || {};

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <Modal.Header>
        <Typography variant="h3">Onboarding Data</Typography>
      </Modal.Header>
      <Modal.Body>
        <div className="h-full flex flex-col">
          <Tabs
            value={selectedTab}
            onChange={(value) => setSelectedTab(value as TabValue)}
            className="h-full flex flex-col"
          >
            <Tabs.List className="sticky top-0 bg-white dark:bg-transparent z-10">
              <div className="flex gap-2 md:gap-4 border-b border-gray-200 dark:border-white/10 overflow-x-auto scrollbar-custom">
                {tabs.map((tab) => (
                  <button
                    key={tab.value}
                    role="tab"
                    onClick={() => setSelectedTab(tab.value as TabValue)}
                    className={`px-2 md:px-4 py-2 text-sm font-medium whitespace-nowrap rounded-t-lg flex items-center gap-2 ${
                      selectedTab === tab.value
                        ? 'bg-primary text-black'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10'
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </div>
            </Tabs.List>

            <div className="flex-grow overflow-y-auto scrollbar-custom pt-4">
              {/* Website & Branding Tab */}
              <Tabs.Panel value="website">
                <WebsiteBrandingSection
                  selectedOrg={selectedOrg}
                  onboardingSetup={onboardingSetup}
                  onboardingData={onboardingData}
                  asTab={true}
                  onRefresh={onRefresh}
                />
              </Tabs.Panel>

              {/* AI Assistant Tab */}
              <Tabs.Panel value="ai">
                <AIAssistantSection
                  selectedOrg={selectedOrg}
                  onboardingSetup={onboardingSetup}
                  onboardingData={onboardingData}
                  asTab={true}
                />
              </Tabs.Panel>

              {/* IDX/MLS Tab */}
              <Tabs.Panel value="idx">
                <IDXMLSSection
                  selectedOrg={selectedOrg}
                  onboardingSetup={onboardingSetup}
                  onboardingData={onboardingData}
                  asTab={true}
                />
              </Tabs.Panel>

              {/* CRM Tab */}
              <Tabs.Panel value="crm">
                <CRMSection
                  selectedOrg={selectedOrg}
                  onboardingSetup={onboardingSetup}
                  onboardingData={onboardingData}
                  asTab={true}
                />
              </Tabs.Panel>

              {/* Strategy Call Tab */}
              <Tabs.Panel value="crashCourse">
                <CrashCourseSection
                  selectedOrg={selectedOrg}
                  onboardingSetup={onboardingSetup}
                  onboardingData={onboardingData}
                  asTab={true}
                  onRefresh={onRefresh}
                />
              </Tabs.Panel>
            </div>
          </Tabs>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
